/**
 * Reference-voice fader — Pro Tools-style vertical aluminum fader.
 *
 * Vertical orientation (default): a real DAW console fader with metallic rail,
 * dB tick marks (-∞, -60, -40, -20, -12, -6, 0, +3, +6), highlighted unity (0),
 * silver handle, LCD-style dB readout above. Mute button below in PT chrome
 * style (pressed = inset + amber glow).
 *
 * Horizontal orientation kept for inline mobile use.
 *
 * Persists to localStorage via the recording store. Affects only ref-voice
 * playback level — does not impact recorded input.
 */
import { Slider } from '@ohear/ui'
import { cx } from './cx'
import { REF_VOICE_MAX_DB, REF_VOICE_MIN_DB, useRecordingStore } from './state'

export interface RefVoiceFaderProps {
  orientation?: 'vertical' | 'horizontal'
}

const TICKS = [REF_VOICE_MAX_DB, 3, 0, -6, -12, -20, -40, -60, REF_VOICE_MIN_DB]

export function RefVoiceFader({ orientation = 'vertical' }: RefVoiceFaderProps) {
  const volumeDb = useRecordingStore((s) => s.refVoiceVolumeDb)
  const muted = useRecordingStore((s) => s.refVoiceMuted)
  const setVol = useRecordingStore((s) => s.setRefVoiceVolume)
  const toggleMute = useRecordingStore((s) => s.toggleRefVoiceMute)

  const valueLabel = muted ? '−∞' : `${volumeDb >= 0 ? '+' : '−'}${Math.abs(volumeDb)}`

  if (orientation === 'horizontal') {
    return (
      <div className="flex items-center gap-2" data-tour="ref-fader">
        <button
          type="button"
          onClick={toggleMute}
          aria-pressed={muted}
          aria-label={muted ? 'Unmute reference voice' : 'Mute reference voice'}
          title={muted ? 'Unmute reference voice' : 'Mute reference voice'}
          className={cx('pt-btn pt-btn--armed flex h-7 w-9 items-center justify-center text-xs')}
        >
          {muted ? 'MUTE' : 'ON'}
        </button>
        <Slider
          label="Reference voice volume"
          min={REF_VOICE_MIN_DB}
          max={REF_VOICE_MAX_DB}
          step={1}
          value={volumeDb}
          onChange={(e) => setVol(Number(e.currentTarget.value))}
          disabled={muted}
          className="w-24"
        />
        <span
          className="pt-lcd pt-lcd--amber text-[11px]"
          style={{ minWidth: 52 }}
          aria-live="polite"
        >
          {valueLabel} dB
        </span>
      </div>
    )
  }

  // Vertical aluminum console fader.
  // The rail is the metallic gradient; the rotated <Slider> sits inside it so
  // we keep our accessible primitive while looking like a real DAW fader.
  return (
    <div
      data-tour="ref-fader"
      className="flex h-full flex-col items-center justify-between rounded-md border border-pt-border bg-gradient-to-b from-pt-panel2 to-pt-panel px-1 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(0,0,0,0.18)]"
      style={{ minHeight: 240 }}
    >
      {/* Channel label up top — PT-style. */}
      <div className="pt-mono text-[9px] uppercase tracking-[0.18em] text-pt-text">REF</div>

      {/* LCD dB readout. */}
      <div
        className="pt-lcd pt-lcd--amber my-1 text-[10px]"
        aria-live="polite"
        title="Reference voice level"
        style={{ minWidth: 38, textAlign: 'center' }}
      >
        {valueLabel}
      </div>

      {/* Fader rail with tick marks + rotated slider. */}
      <div className="relative flex flex-1 items-stretch justify-center py-2">
        {/* Tick marks column on the right of the rail (visually at left after rotation visual). */}
        <div className="relative flex flex-col justify-between py-1 pr-1 pt-mono text-[8px] text-pt-text-soft">
          {TICKS.map((db, i) => (
            <span key={`${db}-${i}`} className={cx(db === 0 ? 'font-bold text-pt-text' : '')}>
              {db === REF_VOICE_MIN_DB ? '−∞' : db > 0 ? `+${db}` : db}
            </span>
          ))}
        </div>
        {/* Aluminum rail. */}
        <div className="relative pt-fader-rail mx-1 my-1" style={{ minHeight: 140 }}>
          {/* Tick marks on rail. */}
          {TICKS.map((db, i) => {
            const pct = (db - REF_VOICE_MIN_DB) / (REF_VOICE_MAX_DB - REF_VOICE_MIN_DB)
            const top = `${(1 - pct) * 100}%`
            return (
              <span
                key={`${db}-${i}`}
                className={cx('pt-fader-tick', db === 0 && 'pt-fader-tick--unity')}
                style={{ top, transform: 'translateY(-50%)' }}
              />
            )
          })}
          {/* Rotated accessible slider — sits inside the rail so the thumb
              tracks vertical motion. */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ overflow: 'visible' }}
          >
            <div
              className="origin-center"
              style={{ transform: 'rotate(-90deg)', width: 140, height: 24 }}
            >
              <Slider
                label="Reference voice volume"
                min={REF_VOICE_MIN_DB}
                max={REF_VOICE_MAX_DB}
                step={1}
                value={volumeDb}
                onChange={(e) => setVol(Number(e.currentTarget.value))}
                disabled={muted}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="pt-mono text-[9px] uppercase tracking-[0.18em] text-pt-text-soft">dB</div>

      {/* Mute button — PT chrome with armed (yellow glow) when muted. */}
      <button
        type="button"
        onClick={toggleMute}
        aria-pressed={muted}
        aria-label={muted ? 'Unmute reference voice' : 'Mute reference voice'}
        title={muted ? 'Unmute reference voice' : 'Mute reference voice'}
        className={cx(
          'pt-btn pt-btn--armed mt-2 flex h-7 w-10 items-center justify-center text-[10px]',
        )}
      >
        {muted ? 'MUTE' : 'ON'}
      </button>
    </div>
  )
}
